

import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaDownload, FaPencilAlt } from 'react-icons/fa';
import { useEffect, useMemo, useState } from 'react';
import { useListDocuments, useListDocumentTypes, useUpdateDocument } from "../../queries/documentRepository";
import { ActionButton } from '../../components/Forms/ActionButton';
import { useHeader } from "../../context/HeaderContext";
import NewDropdown from '../../components/Forms/NewDropdown';
import Modal from '../Submissions/components/Modal';
import { useForm } from 'react-hook-form';

const DocumentList = () => {
    const navigate = useNavigate();
    const { data, isPending } = useListDocuments();
    const { data: documentTypes, isLoading: isDocumentTypesPending } = useListDocumentTypes();
    const { mutate: updateDocument, isPending: isUpdatingDocument } = useUpdateDocument();
    
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        fromDate: '',
        toDate: '',
        name: '',
        module: '',
        documentType: null,
    });

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const { setHeaderTitle } = useHeader();

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            name: '',
            documentType: null,
        },
    });

    useEffect(() => {
        setHeaderTitle('Document Repository');
        return () => {
            setHeaderTitle('');
        };
    }, [setHeaderTitle]);

    useEffect(() => {
        if (data?.documents) {
            setFilteredData(data.documents);
        }
    }, [data]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleDropdownChange = (name) => (selectedOption) => {
        setFilters((prev) => ({ ...prev, [name]: selectedOption.value }));
    };

    const handleEditDropdownChange = (selectedOption) => {
        setValue("documentType", selectedOption.value);
    };

    const handleUpdateDocument = (document) => {
        setSelectedDocument(document);
        setValue("name", document.name || '');
        setValue("documentType", document.documentType?._id || null);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedDocument(null);
        reset();
    };

    const handleEditFormSubmit = (formData) => {
        if (!selectedDocument) return;

        // Validate document type selection
        if (!formData.documentType) {
            toast.error("Please select a document type");
            return;
        }

        const updateData = {
            id: selectedDocument._id,
            name: formData.name,
            documentType: formData.documentType,
        };

        updateDocument(updateData, {
            onSuccess: (res) => {
                if (res.error) {
                    toast.error("Failed to update document");
                    return;
                }
                toast.success("Document updated successfully");
                handleCloseEditModal();
            },
            onError: (error) => {
                console.error("Error updating document:", error);
                const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
                toast.error("Failed to update document: " + errorMessage);
            }
        });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = data?.documents || [];

        if (filters.fromDate) {
            filtered = filtered.filter((document) =>
                new Date(document.createdAt) >= new Date(filters.fromDate)
            );
        }

        if (filters.toDate) {
            filtered = filtered.filter((document) =>
                new Date(document.createdAt) <= new Date(filters.toDate)
            );
        }

        if (filters.name) {
            filtered = filtered.filter((document) =>
                document.name.toLowerCase().includes(filters.name.toLowerCase())
            );
        }

        if (filters.module) {
            filtered = filtered.filter((document) =>
                document.module?.name?.toLowerCase().includes(filters.module.toLowerCase())
            );
        }

        if (filters.documentType) {
            filtered = filtered.filter((document) =>
                document.documentType?._id === filters.documentType
            );
        }

        setFilteredData(filtered);
    };

    const handleViewDocument = (url) => {
        window.open(url, '_blank');
    };

    const handleDownloadDocument = (url, name) => {
        try {
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            toast.error("Failed to download document");
        }
    };

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('name', {
            header: 'Document Name',
            cell: info => (
                <div className="max-w-xs truncate" title={info.getValue()}>
                    {info.getValue()}
                </div>
            ),
        }),
        columnHelper.accessor('module.name', {
            header: 'Module',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('documentType.name', {
            header: 'Document Type',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('documentUrl', {
            header: 'File Type',
            align: 'center',
            cell: info => {
                const url = info.getValue();
                if (!url) return 'N/A';

                const extension = url.split('.').pop()?.toUpperCase();
                const getFileTypeColor = (ext) => {
                    switch (ext) {
                        case 'PDF': return 'red';
                        case 'DOC':
                        case 'DOCX': return 'blue';
                        case 'XLS':
                        case 'XLSX': return 'green';
                        case 'PPT':
                        case 'PPTX': return 'orange';
                        default: return 'gray';
                    }
                };

                return (
                    <Chip
                        value={extension || 'FILE'}
                        color={getFileTypeColor(extension)}
                        small={true}
                    />
                );
            },
        }),
        columnHelper.accessor('createdAt', {
            header: 'Upload Date',
            cell: info => {
                const date = new Date(info.getValue());
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
            },
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            align: 'center',
            enableColumnFilter: false,
            cell: info => {
                const documentUrl = info.row.original?.documentUrl;
                const documentName = info.row.original?.name;
                const document = info.row.original;

                return (
                    <div className='flex items-center justify-center gap-2'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] min-h-7 min-w-7"
                            onClick={() => handleViewDocument(documentUrl)}
                            title="View Document"
                        >
                            <FaEye className="fill-primary" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,89,199,0.1)] hover:bg-[rgba(52,89,199,0.2)] min-h-7 min-w-7"
                            onClick={() => handleUpdateDocument(document)}
                            title="Edit Document Type"
                        >
                            <FaPencilAlt className="fill-chipBlue" />
                        </button>
                    </div>
                )
            },
        }),
    ], []);

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">Document Repository</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] "
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                From Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="fromDate"
                                    value={filters.fromDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                To Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="toDate"
                                    value={filters.toDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="name" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Document Name
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="name"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    placeholder="Enter Document Name"
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="module" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Module
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="module"
                                    value={filters.module}
                                    onChange={handleFilterChange}
                                    placeholder="Enter Module Name"
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="documentType" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Document Type
                            </label>
                            <div className="mt-2.5 w-full">
                                <NewDropdown
                                    placeholder="Select Document Type"
                                    dataList={documentTypes?.map(type => ({
                                        value: type._id,
                                        label: type.name
                                    })) || []}
                                    value={filters.documentType}
                                    onChange={handleDropdownChange('documentType')}
                                    disabled={isDocumentTypesPending}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            {/* Empty div for spacing */}
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                fromDate: '',
                                toDate: '',
                                name: '',
                                module: '',
                                documentType: null,
                            });
                            setFilteredData(data?.documents || []);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full pl-6">
                    <h1 className="self-start text-lg font-bold pb-4">Document Repository</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No documents available
                        </div>
                    )}
                </div>
            </section>

            {/* Edit Document Modal */}
            <Modal isOpen={showEditModal} onClose={handleCloseEditModal} title="Edit Document">
                <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-full">
                        <h2 className="text-lg font-bold mb-4">Edit Document</h2>
                        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleEditFormSubmit)}>
                            <div className="flex flex-col gap-0">
                                <span>Document Name</span>
                                <input
                                    type="text"
                                    placeholder="Enter document name"
                                    className="border border-navBtn rounded-md p-2"
                                    {...register("name", {
                                        required: "Document name is required",
                                    })}
                                />
                                {errors.name && <span className="text-red">{errors.name.message}</span>}
                            </div>
                            <div className="flex flex-col gap-0">
                                <span>Document Type</span>
                                <div className="mt-2">
                                    <NewDropdown
                                        placeholder="Select Document Type"
                                        dataList={documentTypes?.map(type => ({
                                            value: type._id,
                                            label: type.name
                                        })) || []}
                                        value={watch("documentType")}
                                        onChange={handleEditDropdownChange}
                                        disabled={isDocumentTypesPending}
                                    />
                                </div>
                                {errors.documentType && <span className="text-red">{errors.documentType.message}</span>}
                            </div>
                            <div className="flex justify-end gap-3">
                                <ActionButton
                                    variant={'cancel'}
                                    text={'Cancel'}
                                    handleSubmit={handleCloseEditModal}
                                />
                                <ActionButton
                                    variant={'submit'}
                                    text={'Update'}
                                    type={'submit'}
                                    disabled={isUpdatingDocument}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>

            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </main>
    );
}

export default DocumentList;